import React, { useEffect, useState } from 'react';
import { Refresh, ErrorOutline, Tune } from '@mui/icons-material';
import SearchBar from '../components/SearchBar';
import FilterModal from '../components/FilterModal';
import SortButton from '../components/SortButton';
import BackWithBreadcrumbAppbar from '../components/BackWithBreadcrumbAppbar';
import { useQuery, gql, useMutation } from '@apollo/client';
import LoadingPage from './LoadingPage';
import { useTranslation } from 'react-i18next';
import FilteredProducts from '../components/FilteredProducts';
import FilterButton from '../components/FilterButton';
import AddIcon from '@mui/icons-material/Add';


import {
    Grid,
    useTheme,
    Typography,
    Box,
    Menu,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    AlertTitle,
    IconButton,
    CircularProgress,
    Fade,
    Button,
    Fab, styled, keyframes,
    useMediaQuery
} from '@mui/material';
import useUserData from '../hooks/useUserData';
import { UPLOAD_IMAGES_API_URL } from '../constant';
import AddProductForm from '../components/AddProductForm';
import ProductForm from '../components/AddProductForm';
import PropTypes from 'prop-types';


const GET_ALL_PRODUCTS = gql`
  query GetAllProducts {
    getAllProducts {
      id
      product_name
      name
      designation
      company
      country
      role
      email
      stallNo
      bio
      product_image
      product_description
      exhibitor_id
      isConnected
      created
      profile_pic
    }
  }
`;
const UPLOAD_PRODUCT = gql`
mutation UploadProduct($user_id: ID!, $user_role: String!, $product_name: String!, $product_description: String!) {
    uploadProduct(user_id: $user_id, user_role: $user_role, product_name: $product_name, product_description: $product_description) {
        id
        success
        message
    }
}
`;
const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const ShinyFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    animation: `${shimmer} 50s infinite linear`,
    backgroundSize: '15% auto',
    transition: 'transform 0.3s, box-shadow 0.3s',
    backdropFilter: 'blur(10px)',
    '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: theme.shadows[10]
    }
}));
const MyProductList = ({ user }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        role: [],
        company: [],
        designation: [],
        country: []
    });
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const roleType = "All";

    const [formOpen, setFormOpen] = useState(false);
    const [newProduct, setNewProduct] = useState({
        product_name: '',
        description: '',
        product_image: null
    });
    const [uploading, setUploading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');


    const { loading, error, data, refetch } = useQuery(GET_ALL_PRODUCTS, {
        variables: { roleType },
        notifyOnNetworkStatusChange: true,
        skip: !roleType,
        fetchPolicy: 'cache-and-network'
    });
    const [uploadProduct, { loading: uploadLoading, error: uploadError }] = useMutation(UPLOAD_PRODUCT);

    const handleAddProduct = async (formData) => {
        await uploadProduct({
            variables: {
                user_id: user?.id,
                user_role: user?.role,
                product_name: formData.product_name,
                product_description: formData.product_description
            }
        }).then(async (res) => {
            if (res?.data?.uploadProduct?.success) {
                const product_id = res?.data?.uploadProduct?.id;
                const imageFile = formData.product_image;
                const formDataImage = new FormData();
                formDataImage.append("image", imageFile);

                const imageResponse = await fetch(`${UPLOAD_IMAGES_API_URL}/upload/product/${user.eventId}/${user.role}/${product_id}`, {
                    method: 'POST',
                    body: formDataImage, // Use FormData here
                });

                const imageData = await imageResponse.json();
                if (imageData.success) {
                    setSuccessMessage(t('productAddedSuccessfully'));
                    setTimeout(() => {
                        setSuccessMessage('');
                        setFormOpen(false);
                        refetch();
                    }, 2000);
                } else {
                    setErrorMessage(t('errorUploadingImage'));
                    setTimeout(() => {
                        setErrorMessage('');
                    }, 2000);
                }
            } else {
                setErrorMessage(t('errorAddingProduct'));
                setTimeout(() => {
                    setErrorMessage('');
                }, 2000);

            }

        }).catch(() => {
            setErrorMessage(t('errorAddingProduct'));
            setTimeout(() => {
                setErrorMessage('');
            }, 2000);
        }).finally(() => {
            setUploading(false);
        });

    };

    useEffect(() => {
        if (data?.getAllProducts) {
            const filteredData = data.getAllProducts.filter(product => parseInt(product.exhibitor_id) === parseInt(user.id));
            setProducts(filteredData);
            setFilteredProducts(filteredData);
        }
    }, [data, user.id]);

    const [sortAnchorEl, setSortAnchorEl] = useState(null);
    const [sortOption, setSortOption] = useState('');

    useEffect(() => {
        let filtered = [...products];

        // Apply search first
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchLower)
            );
        }

        // Apply filters
        if (filters.role.length > 0) {
            filtered = filtered.filter(p => filters.role.includes(p.role));
        }
        if (filters.company.length > 0) {
            filtered = filtered.filter(p => filters.company.includes(p.company));
        }
        if (filters.designation.length > 0) {
            filtered = filtered.filter(p => filters.designation.includes(p.designation));
        }
        if (filters.country.length > 0) {
            filtered = filtered.filter(p => filters.country.includes(p.country));
        }

        // Apply sorting
        if (sortOption) {
            filtered.sort((a, b) => {
                let field;
                if (sortOption === 'name') {
                    field = 'name';
                } else if (sortOption === 'company') {
                    field = 'company';
                } else if (sortOption === 'designation') {
                    field = 'designation';
                } else {
                    field = 'country';
                }
                return a[field].localeCompare(b[field]);
            });
        }

        setFilteredProducts(filtered);
        setCurrentPage(1);
    }, [products, searchQuery, filters, sortOption]);

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setFilterModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchQuery(event);
    };

    const handleFilter = () => {
        setFilterModalOpen(true);
    };

    const handleSortClick = (event) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setSortAnchorEl(null);
    };

    const handleSortSelect = (option) => {
        setSortOption(option);
        setSortAnchorEl(null);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
    const handleRetry = () => {
        refetch();
    };

    if (error) {
        return (

            <Box sx={{ p: 3, height: '100vh' }}>
                <Alert
                    severity="error"
                    sx={{
                        mt: 4,
                        borderRadius: 2,
                        boxShadow: theme.shadows[2]
                    }}
                    action={
                        <IconButton color="inherit" onClick={handleRetry}>
                            <Refresh />
                        </IconButton>
                    }
                >
                    <AlertTitle>{t('Error')}</AlertTitle>
                    {t('404')} ({error?.message || t("SomethingWentWrong")})
                </Alert>
            </Box>
        );
    }

    return (

        <Box sx={{
            padding: theme.spacing(3),
            position: 'relative',
            maxWidth: 1600,
            margin: '0 auto'
        }}>
            <Fade in={!loading} timeout={300}>
                <Box>
                    <Typography
                        variant="h3"
                        align="center"
                        sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            mb: 3,
                            textShadow: `0 2px 4px ${theme.palette.action.hover}`
                        }}
                    >
                        {t('productsList')}
                    </Typography>

                    <Grid container spacing={1.5} alignItems="center" sx={{
                        position: 'sticky',
                        top: isMobile?100:130,
                        zIndex: 10,
                        backgroundColor: theme.palette.background.paper,
                        py: 2,
                        px: 1,
                        boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        marginBottom: 3,
                        transition: 'all 0.2s ease',
                    }}>
                        {/* Search Bar */}
                        <Grid item xs={12} md={4} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative'
                        }}>
                            <SearchBar
                                role="products"
                                onSearch={handleSearch}
                                products={products}
                                placeholder={t('searchProducts')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        backgroundColor: theme.palette.background.default,
                                        border: `1px solid ${theme.palette.divider}`,
                                        transition: theme.transitions.create(['border-color', 'box-shadow']),
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: `${theme.shadows[2]}`,
                                            borderColor: theme.palette.primary.main,
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Filter & Sort Controls */}
                        <Grid item xs={6} md={4} sx={{
                            display: 'flex',
                            gap: 1.5,
                            justifyContent: 'center'
                        }}>
                            {/* Filter Button */}
                            <Box sx={{ flex: 1 }}>
                                <FilterButton
                                    onFilter={handleFilter}
                                    badgeContent={Object.values(filters).flat().length}
                                    sx={{
                                        width: '100%',
                                        borderRadius: 3,
                                        py: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                        }
                                    }}
                                />
                            </Box>

                            {/* Sort Button */}
                            <Box sx={{ flex: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleSortClick}
                                    startIcon={<Tune fontSize="small" />}
                                    sx={{
                                        width: '100%',
                                        borderRadius: 3,
                                        py: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                        }
                                    }}
                                >
                                    {t('sort')}
                                </Button>
                                <Menu
                                    anchorEl={sortAnchorEl}
                                    open={Boolean(sortAnchorEl)}
                                    onClose={handleSortClose}
                                    PaperProps={{
                                        sx: {
                                            borderRadius: 2,
                                            boxShadow: theme.shadows[3],
                                            minWidth: 150,
                                            mt: 1,
                                            border: `1px solid ${theme.palette.divider}`
                                        }
                                    }}
                                >
                                    {['name', 'company', 'country'].map((option) => (
                                        <MenuItem
                                            key={option}
                                            onClick={() => handleSortSelect(option)}
                                            selected={sortOption === option}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: theme.palette.action.selected,
                                                    fontWeight: 500
                                                },
                                                py: 1,
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {t(option)}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                        </Grid>

                        {/* Items Per Page Selector */}
                        <Grid item xs={6} md={4} sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', md: 'flex-end' }
                        }}>
                            <FormControl variant="outlined" sx={{
                                minWidth: 150,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    backgroundColor: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                    }
                                }
                            }}>
                                <InputLabel shrink>{t('itemsPerPage')}</InputLabel>
                                <Select
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    label={t('itemsPerPage')}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            py: 1.1,
                                            pl: 2
                                        }
                                    }}
                                >
                                    {[10, 25, 50, 100].map(value => (
                                        <MenuItem
                                            key={value}
                                            value={value}
                                            sx={{
                                                borderRadius: 1,
                                                m: 0.5,
                                                '&.Mui-selected': {
                                                    backgroundColor: `${theme.palette.action.selected} !important`
                                                }
                                            }}
                                        >
                                            {value}
                                        </MenuItem>
                                    ))}
                                    <MenuItem
                                        value={products.length}
                                        sx={{
                                            borderRadius: 1,
                                            m: 0.5,
                                            borderTop: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        {t('all')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <FilteredProducts
                        role="my_products"
                        refresh={refetch}
                        filteredProducts={filteredProducts}
                        paginatedProducts={paginatedProducts}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        setFilteredProducts={setFilteredProducts}
                        user={user}
                    />

                    <FilterModal
                        open={filterModalOpen}
                        onClose={() => setFilterModalOpen(false)}
                        onApply={handleFilterApply}
                        filters={filters}
                        attendees={products}
                        roleName="products"
                    />
                    <ShinyFab color="primary" onClick={() => setFormOpen(true)}>
                        <AddIcon />
                    </ShinyFab>

                    <ProductForm
                        open={formOpen}
                        onClose={() => setFormOpen(false)}
                        onSubmit={handleAddProduct}
                        product={newProduct}
                        setProduct={setNewProduct}
                        uploading={uploading}
                        errorMessage={errorMessage}
                        successMessage={successMessage}
                        user={user}
                        uploadLoading={uploadLoading}
                        uploadError={uploadError}
                        mode = 'create'
                    />
                </Box>
            </Fade>

            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    zIndex: theme.zIndex.modal
                }}>
                    <CircularProgress
                        size={80}
                        thickness={2}
                        sx={{
                            color: theme.palette.primary.main,
                            animationDuration: '800ms'
                        }}
                    />
                </Box>
            )}
        </Box>

    );
};

export default MyProductList;
MyProductList.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired,
        eventId: PropTypes.string.isRequired,
        company: PropTypes.string.isRequired
    }).isRequired
};